// Ported from dev-scrips-pm-react services/scheduling/use-appointment-details.ts (PROD-1190 calendar port)
import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  PreLoadAppointmentRequest,
  PreLoadAppointmentResponse,
} from '@/types/scheduling-api';

/**
 * POST /api/v1/Practice/PreLoadAppointmentDetails
 *
 * Powers the appointment-popup-card opened by clicking any appointment
 * in the calendar. Mirrors Flutter's `PreLoadAppointmentEvent` flow:
 * `appointment_popup_card.dart:99-109` → `scheduling_bloc.dart:1183-1199`
 * → `scheduling_data_source_impl.dart:945-977`.
 *
 * Although the verb is POST, this is a read-style call (no side effect on
 * the backend) — wired as `useQuery` so rapid re-clicks of the same
 * appointment serve from cache. Cache key includes all 5 ids so different
 * appointments don't collide.
 *
 * Wire-shape (dev-curl verified 2026-05-13):
 *   {
 *     appointmentId, patientId, providerId,
 *     billing: {} | null,   // {} = load invoice, null = skip
 *     patient: {} | null,   // {} = load patient, null = skip
 *     appointmentProfileId
 *   }
 *
 * For the popup we always ship `billing: {}` and `patient: {}` because
 * the right-side Payment panel needs invoice data and the left side
 * needs full patient data (insurance, language, identification, etc.).
 *
 * AbortSignal is forwarded per the project rule (every queryFn must
 * destructure { signal } and pass it through — see
 * feedback_signal_forwarding.md).
 */

export interface UseAppointmentDetailsParams {
  appointmentId: string;
  patientId: string;
  providerId: string;
  appointmentProfileId: string;
  /** Set to false when popup is closed; query stays in cache but doesn't refetch. */
  enabled?: boolean;
}

export function useAppointmentDetails(params: UseAppointmentDetailsParams) {
  const {
    appointmentId,
    patientId,
    providerId,
    appointmentProfileId,
    enabled = true,
  } = params;

  return useQuery<PreLoadAppointmentResponse | null>({
    queryKey: [
      'appointment-details',
      appointmentId,
      patientId,
      providerId,
      appointmentProfileId,
    ],
    enabled:
      enabled &&
      !!appointmentId &&
      !!patientId &&
      !!providerId &&
      !!appointmentProfileId,
    queryFn: async ({ signal }) => {
      const body: PreLoadAppointmentRequest = {
        appointmentId,
        patientId,
        providerId,
        billing: {},
        patient: {},
        appointmentProfileId,
      };
      // PreLoadAppointmentDetails can answer 200 with an EMPTY body for some
      // appointments; the api-client then returns undefined, which React Query
      // v5 rejects ("Query data cannot be undefined") → the popup showed a raw
      // error. Coerce to null so it's a clean empty result, not an error.
      const res = await post<PreLoadAppointmentResponse>(
        '/api/v1/Practice/PreLoadAppointmentDetails',
        body,
        { signal },
      );
      return res ?? null;
    },
    // Appointment data drifts when status / payments change; keep cache
    // short. SignalR refreshes invalidate via the parent calendar queries
    // already, so this is mostly a click-debounce window.
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}
